import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

export default function TruckingBillingPrintPage() {
  const { printToken } = useParams();
  const [bill, setBill]         = useState(null);
  const [lines, setLines]       = useState([]);
  const [storeInfo, setStoreInfo] = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    truckingBillAPI.getByPrintToken(printToken)
      .then((res) => {
        setBill(res.data.data.bill);
        setLines(res.data.data.lines || []);
        setStoreInfo(res.data.data.storeInfo || {});
      })
      .catch(() => setError('Failed to load billing statement.'))
      .finally(() => setLoading(false));
  }, [printToken]); // eslint-disable-line

  useEffect(() => {
    if (!bill || loading) return;
    document.title = `TruckingBill-${bill.BillName}`;
    const t = setTimeout(() => window.print(), 400);
    return () => clearTimeout(t);
  }, [bill, loading]);

  if (loading) return <p style={{ padding: 40 }}>Loading…</p>;
  if (error)   return <p style={{ padding: 40, color: '#c00' }}>{error}</p>;

  const rows = lines.map((l) => {
    const wb          = l.WayBillId || {};
    const stripping   = parseFloat(wb.StrippingFee || 0) + parseFloat(wb.AdditionalStrippingFee || 0);
    const total       = parseFloat(l.SubTotal || 0);
    const truckingRate = Math.max(total - stripping, 0);
    const vessel      = [wb.Vessel, wb.Voyage].filter(Boolean).join(' / ');
    return {
      date:         wb.TransactionDate,
      plateNo:      wb.PlateNo || '—',
      // Column header stays "WB NO.", but the value shown is the waybill's
      // reference number — that is the identifier the carrier reconciles against.
      wbNo:         wb.referenceNumber || '—',
      vesselVoyage: vessel || '—',
      vanNo:        wb.VanNo || '—',
      consignee:    wb.CustomerId?.CustomerName || wb.CustomerId?.name || '—',
      location:     wb.CustomerAddress || '—',
      truckingRate,
      total,
    };
  });

  // Group rows by Vessel / Voyage No. (order of first appearance) for per-span subtotals
  const spanGroups = [];
  const spanIndex = new Map();
  for (const r of rows) {
    const key = r.vesselVoyage;
    if (!spanIndex.has(key)) {
      spanIndex.set(key, spanGroups.length);
      spanGroups.push({ key, rows: [], subtotal: 0 });
    }
    const group = spanGroups[spanIndex.get(key)];
    group.rows.push(r);
    group.subtotal += r.total;
  }

  const grandTotal       = rows.reduce((s, r) => s + r.total, 0);
  const netOfVAT         = grandTotal / 1.12;
  const vat              = grandTotal - netOfVAT;
  const cwt               = netOfVAT * 0.02;
  const amountDue        = grandTotal - cwt;

  const billDate = bill.BillDate
    ? new Date(bill.BillDate).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : fmtDate(new Date());

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #000; padding: 3px 5px; font-size: 9pt; }
        .void-watermark {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 96pt; font-weight: 900;
          color: rgba(220,38,38,0.18);
          pointer-events: none; z-index: 1000; user-select: none; white-space: nowrap;
        }
      `}</style>

      {bill?.isVoided && <div className="void-watermark">VOID</div>}

      {/* Company Header */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <p style={{ fontWeight: 'bold', fontSize: '13pt', margin: '0 0 2px' }}>
          { 'ARVANNAH RENZ TRUCKING INC.'}
        </p>
        <p style={{ margin: '0 0 2px', fontSize: '9pt' }}>
          { 'Q. ABETO ST. BRGY. ABETO-MIRASOL TAFT SOUTH MANDURIAO, ILOILO CITY'}
        </p>
        <p style={{ margin: '0 0 2px', fontSize: '9pt' }}>
          {storeInfo.tin ? `TIN NO. ${storeInfo.tin}` : 'TIN NO. 768-950-988-000-VAT'}
        </p>
        <p style={{ fontWeight: '600', margin: '4px 0 0', fontSize: '10pt' }}>
          STATEMENT OF ACCOUNT NO.&nbsp;{bill.SOANumber || '___________'}
        </p>
      </div>

      {/* Addressed-to + Date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: '10pt' }}>
        <div>
          {bill.CarrierName && <p style={{ fontWeight: '600', margin: '0 0 2px' }}>{bill.CarrierName}</p>}
          {bill.CarrierAddress && <p style={{ margin: 0 }}>{bill.CarrierAddress}</p>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0 }}>Date: <strong>{billDate}</strong></p>
        </div>
      </div>

      {/* Main Table */}
      <div style={{ marginBottom: 20 }}>
        <table>
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th>DATE</th>
              <th>PLATE NO.</th>
              <th>WB NO.</th>
              <th>VESSEL / VOY. NO.</th>
              <th>VAN NO.</th>
              <th>CONSIGNEE</th>
              <th>LOCATION</th>
              <th style={{ textAlign: 'right' }}>RATES</th>
              <th style={{ textAlign: 'right' }}>TOTAL AMOUNT PER SPAN</th>
              <th>DATE RETURN</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 12 }}>No records.</td></tr>
            ) : spanGroups.map((group, gi) => (
              <React.Fragment key={gi}>
                {group.rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                    <td>{r.plateNo}</td>
                    <td>{r.wbNo}</td>
                    <td>{r.vesselVoyage}</td>
                    <td>{r.vanNo}</td>
                    <td>{r.consignee}</td>
                    <td>{r.location}</td>
                    <td style={{ textAlign: 'right' }}>₱{PHP(r.truckingRate)}</td>
                    <td style={{ textAlign: 'right' }}>₱{PHP(r.total)}</td>
                    <td>—</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <td colSpan={8} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    SUBTOTAL — {group.key}:
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>₱{PHP(group.subtotal)}</td>
                  <td></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Financial Summary */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
        <table style={{ width: 'auto', borderCollapse: 'collapse' }}>
          <tbody>
            {[
              { label: 'TOTAL:',       value: grandTotal },
              { label: 'VAT:',         value: vat },
              { label: 'Net of VAT:',  value: netOfVAT },
              { label: 'CWT:',         value: cwt },
            ].map((row) => (
              <tr key={row.label}>
                <td style={{ border: 'none', paddingRight: 32, paddingTop: 2, paddingBottom: 2, textAlign: 'left', color: '#555' }}>{row.label}</td>
                <td style={{ border: 'none', textAlign: 'right', fontWeight: '500', minWidth: 110 }}>₱{PHP(row.value)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #000' }}>
              <td style={{ border: 'none', paddingRight: 32, paddingTop: 4, textAlign: 'left', fontWeight: 'bold' }}>TOTAL AMOUNT DUE:</td>
              <td style={{ border: 'none', textAlign: 'right', fontWeight: 'bold', minWidth: 110 }}>₱{PHP(amountDue)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Signature Block */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10pt', marginTop: 40 }}>
        <div>
          <p style={{ fontWeight: '600', borderTop: '1px solid #000', paddingTop: 4, paddingRight: 64, margin: 0 }}>
            {storeInfo.signatoryName || 'JOHN EFREN OCTAVIANO'}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '9pt', color: '#555' }}>
            {storeInfo.signatoryTitle || 'CHIEF FINANCIAL OFFICER'}
          </p>
        </div>
        <div>
          <p style={{ margin: '0 0 16px' }}>
            RECEIVED BY:&nbsp;
            <span style={{ display: 'inline-block', borderBottom: '1px solid #000', width: 180 }}>&nbsp;</span>
          </p>
          <p style={{ margin: 0 }}>
            Date:&nbsp;
            <span style={{ display: 'inline-block', borderBottom: '1px solid #000', width: 180 }}>&nbsp;</span>
          </p>
        </div>
      </div>

      <div className="no-print" style={{ marginTop: 24 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
